/**
 * Сотрудники: список, карточка, приём и увольнение.
 *
 * ── Что здесь изменилось ────────────────────────────────────────────────────
 *
 * Была таблица на пять колонок, в каждой строке — три выпадающих списка: роль,
 * команда, статус. Строка правилась прямо в таблице, и любое неосторожное
 * движение колесом мыши над списком меняло сотруднику роль. Правка карточки
 * раскрывалась внутри той же ячейки и ломала раскладку остальных строк.
 *
 * Теперь список — это список: имя, роль, команда, состояние. Всё, что меняет
 * сотрудника, живёт в его карточке, которая открывается по нажатию. Случайных
 * изменений там быть не может: карточку надо сперва открыть.
 *
 * ── Про пароль ──────────────────────────────────────────────────────────────
 *
 * Показать текущий пароль нельзя и не будет можно: в базе лежит хеш, и
 * восстановить из него исходный невозможно. Это не ограничение интерфейса, а
 * смысл хеширования — если бы пароль показывался, он хранился бы обратимо, и
 * доступ ко всем учёткам получал бы любой, кто добрался до базы.
 *
 * Поэтому здесь не «посмотреть», а «задать новый». Новый пароль виден ровно
 * один раз — сразу после назначения, чтобы было что передать сотруднику.
 */

import { useMemo, useState } from "react";
import type { UserDto, UserSummaryDto } from "@tw/shared-types";
import type { AccountFolderDto } from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { describeError } from "../lib/errors.js";
import { ROLE_LABELS, ROLE_OPTIONS, roleTone } from "../lib/roles.js";
import { cn } from "@/lib/utils";
import { ALERT, FIELD } from "./controls.js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CopyIcon, PlusIcon, SearchIcon, TrashIcon } from "./icons.tsx";

type AnyUser = UserDto | UserSummaryDto;

interface PeoplePanelProps {
  users: AnyUser[];
  folders: readonly AccountFolderDto[];
  onChanged: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Активен",
  SUSPENDED: "Приостановлен",
  DEACTIVATED: "Отключён",
};

/** Команда «без команды» — не запись в базе, а строка списка. */
const NO_TEAM = "__none__";

export function PeoplePanel({ users, folders, onChanged }: PeoplePanelProps) {
  const { api, can } = useSession();

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [opened, setOpened] = useState<string | null>(null);

  /**
   * Только что назначенный пароль.
   *
   * Держится в памяти экрана и исчезает при перезагрузке: это единственный
   * момент, когда его можно прочитать, и сохранять его куда-либо значило бы
   * завести второе место хранения пароля — ровно то, чего мы избегаем.
   */
  const [freshPassword, setFreshPassword] = useState<{
    id: string;
    value: string;
  } | null>(null);

  const canCreate = can("user.create");
  const canUpdate = can("user.update");
  const canChangeRole = can("user.role.change");
  const canChangeStatus = can("user.delete");
  const canManageTeams = can("telegram.account.update");

  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (err) {
      // Сервер отвергает попытку сменить свою роль, разжаловать последнего
      // админа и удалить сотрудника с историей — сообщение оттуда
      // информативнее любого локального.
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const teams = folders.filter((f) => f.parentId !== null);
  const roots = folders.filter((f) => f.parentId === null);

  const teamName = (id: string | null | undefined): string | null => {
    if (!id) return null;
    const folder = folders.find((f) => f.id === id);
    if (!folder) return null;
    const parent = folder.parentId
      ? folders.find((f) => f.id === folder.parentId)
      : null;
    return parent ? `${parent.name} · ${folder.name}` : folder.name;
  };

  const folderOf = (u: AnyUser): string | null =>
    "folderId" in u ? (u.folderId ?? null) : null;

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();

    return users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;

      if (teamFilter) {
        const folderId = "folderId" in u ? (u.folderId ?? null) : null;
        // «Без команды» — отдельное значение, а не пустой фильтр: иначе
        // единственный способ найти нераспределённых был бы глазами.
        if (teamFilter === NO_TEAM ? folderId !== null : folderId !== teamFilter)
          return false;
      }

      if (!needle) return true;

      const email = "email" in u ? u.email : "";
      return (
        u.displayName.toLocaleLowerCase().includes(needle) ||
        email.toLocaleLowerCase().includes(needle)
      );
    });
  }, [users, search, roleFilter, teamFilter]);

  /*
   * Раскладка по командам.
   *
   * Группируем, только когда показаны все команды сразу: внутри одной
   * заголовок повторял бы её же название над списком.
   */
  const groups = useMemo(() => {
    if (teamFilter !== null) {
      return [{ key: "all", title: "", items: filtered }];
    }

    const byTeam = new Map<string, AnyUser[]>();
    for (const u of filtered) {
      const key = ("folderId" in u ? u.folderId : null) ?? NO_TEAM;
      const list = byTeam.get(key);
      if (list) list.push(u);
      else byTeam.set(key, [u]);
    }

    const ordered: Array<{ key: string; title: string; items: AnyUser[] }> = [];
    for (const folder of folders) {
      const items = byTeam.get(folder.id);
      if (items?.length) {
        ordered.push({
          key: folder.id,
          title: teamName(folder.id) ?? folder.name,
          items,
        });
      }
    }
    // Нераспределённые — последними: это не команда, а остаток.
    const loose = byTeam.get(NO_TEAM);
    if (loose?.length) {
      ordered.push({ key: NO_TEAM, title: "Без команды", items: loose });
    }

    return ordered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, folders, teamFilter]);

  const usedTeams = folders.filter((folder) =>
    users.some((u) => folderOf(u) === folder.id),
  );
  const hasLoose = users.some((u) => !folderOf(u));

  const openedUser = users.find((u) => u.id === opened) ?? null;

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className={ALERT}>
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card p-2.5 shadow-sm">
        <div className="relative min-w-[180px] flex-1">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Имя или email"
            aria-label="Поиск сотрудника"
            className={cn(FIELD, "w-full pl-8")}
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          aria-label="Фильтр по роли"
          className={FIELD}
        >
          <option value="">Все роли</option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        <span className="flex-none text-[12px] text-muted-foreground">
          {filtered.length} из {users.length}
        </span>

        {canCreate && (
          <Button
            variant="brand"
            size="sm"
            onClick={() => setAdding(true)}
            className="flex-none gap-1.5"
          >
            <PlusIcon className="size-4" />
            Сотрудник
          </Button>
        )}
      </div>

      {/* Команды — плашками, а не ещё одним выпадающим списком: их немного, а
          нажатие в один щелчок читается быстрее, чем раскрытие списка. */}
      {(usedTeams.length > 0 || hasLoose) && (
        <div className="flex flex-wrap items-center gap-1.5">
          <TeamChip active={teamFilter === null} onClick={() => setTeamFilter(null)}>
            Все команды
          </TeamChip>
          {usedTeams.map((folder) => (
            <TeamChip
              key={folder.id}
              active={teamFilter === folder.id}
              onClick={() => setTeamFilter(folder.id)}
            >
              {folder.name}
            </TeamChip>
          ))}
          {hasLoose && (
            <TeamChip
              active={teamFilter === NO_TEAM}
              onClick={() => setTeamFilter(NO_TEAM)}
            >
              Без команды
            </TeamChip>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="rounded-2xl border border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground shadow-sm">
          Никто не подходит под фильтр
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-1.5">
              {group.title && (
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {group.title} · {group.items.length}
                </p>
              )}

              <ul className="flex list-none flex-col gap-1.5 p-0">
                {group.items.map((u) => (
                  <li key={u.id}>
                    <PersonRow
                      user={u}
                      team={teamName(folderOf(u))}
                      showTeam={teamFilter !== null}
                      freshPassword={
                        freshPassword?.id === u.id ? freshPassword.value : null
                      }
                      onOpen={() => setOpened(u.id)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <CreateDialog
        open={adding}
        busy={busy}
        onOpenChange={setAdding}
        onSubmit={(input) =>
          void run(async () => {
            await api.createUser(input);
            setFreshPassword(null);
            setAdding(false);
          })
        }
      />

      <PersonDialog
        user={openedUser}
        busy={busy}
        roots={roots}
        teams={teams}
        canUpdate={canUpdate}
        canChangeRole={canChangeRole}
        canChangeStatus={canChangeStatus}
        canManageTeams={canManageTeams}
        freshPassword={
          openedUser && freshPassword?.id === openedUser.id
            ? freshPassword.value
            : null
        }
        onClose={() => setOpened(null)}
        onSave={(patch) =>
          void run(() => api.updateUser(openedUser?.id ?? "", patch))
        }
        onRole={(role) =>
          void run(() => api.changeUserRole(openedUser?.id ?? "", role))
        }
        onTeam={(folderId) =>
          void run(() => api.moveUserToFolder(openedUser?.id ?? "", folderId))
        }
        onStatus={(status) =>
          void run(() => api.setUserStatus(openedUser?.id ?? "", status))
        }
        onPassword={(password) =>
          void run(async () => {
            const id = openedUser?.id ?? "";
            await api.setUserPassword(id, password);
            setFreshPassword({ id, value: password });
          })
        }
        onDelete={() =>
          void run(async () => {
            await api.deleteUser(openedUser?.id ?? "");
            setOpened(null);
          })
        }
      />
    </div>
  );
}

function TeamChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "border-brand bg-secondary text-brand"
          : "border-border bg-card text-muted-foreground hover:bg-secondary",
      )}
    >
      {children}
    </button>
  );
}

function PersonRow({
  user,
  team,
  showTeam,
  freshPassword,
  onOpen,
}: {
  user: AnyUser;
  team: string | null;
  showTeam: boolean;
  freshPassword: string | null;
  onOpen: () => void;
}) {
  const email = "email" in user ? user.email : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-2.5 rounded-xl border border-transparent bg-accent/60 px-3 py-2.5 text-left transition-colors hover:bg-secondary"
    >
      {/*
        Точка ветки перед именем.

        Восемь ролей глазом не различаются: «Стажёр оператора» и «Стажёр
        менеджера» отличаются одним словом в середине. А ветка — главное, что
        нужно понять о сотруднике с первого взгляда: она определяет, с какими
        аккаунтами он работает. Цветов три, по числу веток, а не восемь по
        числу ролей — ступень читается словом и в цвете не нуждается.
      */}
      <span
        aria-hidden
        className="size-2 flex-none rounded-full"
        style={{ background: roleTone(user.role) }}
      />

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-heading">
          {user.displayName}
        </span>
        <span className="block truncate text-[11px] text-muted-foreground">
          {ROLE_LABELS[user.role] ?? user.role}
          {email && ` · ${email}`}
          {showTeam && team && ` · ${team}`}
        </span>
        {freshPassword && (
          <span className="mt-1 inline-block rounded-lg bg-primary/10 px-2 py-1 font-mono text-[12px] text-brand">
            Новый пароль: {freshPassword}
            <span className="ml-1 font-sans text-muted-foreground">
              — виден только сейчас
            </span>
          </span>
        )}
      </span>

      <span
        className={cn(
          "flex-none rounded-full px-2 py-0.5 text-[11px] font-medium",
          user.status === "ACTIVE"
            ? "bg-primary/10 text-brand"
            : "bg-secondary text-muted-foreground",
        )}
      >
        {STATUS_LABELS[user.status] ?? user.status}
      </span>
    </button>
  );
}

/**
 * Карточка сотрудника.
 *
 * Всё, что меняет человека, собрано здесь: имя и почта, роль, команда,
 * состояние, пароль и увольнение. Раньше это было размазано по колонкам
 * таблицы, и половина действий совершалась случайным движением мыши.
 */
function PersonDialog({
  user,
  busy,
  roots,
  teams,
  canUpdate,
  canChangeRole,
  canChangeStatus,
  canManageTeams,
  freshPassword,
  onClose,
  onSave,
  onRole,
  onTeam,
  onStatus,
  onPassword,
  onDelete,
}: {
  user: AnyUser | null;
  busy: boolean;
  roots: readonly AccountFolderDto[];
  teams: readonly AccountFolderDto[];
  canUpdate: boolean;
  canChangeRole: boolean;
  canChangeStatus: boolean;
  canManageTeams: boolean;
  freshPassword: string | null;
  onClose: () => void;
  onSave: (patch: { displayName?: string; email?: string }) => void;
  onRole: (role: string) => void;
  onTeam: (folderId: string | null) => void;
  onStatus: (status: string) => void;
  onPassword: (password: string) => void;
  onDelete: () => void;
}) {
  const email = user && "email" in user ? user.email : null;
  const folderId = user && "folderId" in user ? (user.folderId ?? null) : null;

  const [name, setName] = useState("");
  const [mail, setMail] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  /*
   * Пустые поля означают «не трогали».
   *
   * Показываем в них текущие значения, а отправляем только изменённое. Ключ на
   * содержимом окна (см. key ниже) заставляет React создать поля заново для
   * каждого сотрудника — иначе в карточке второго остались бы значения
   * первого.
   */

  const changed =
    user !== null &&
    ((name !== "" && name !== user.displayName) ||
      (mail !== "" && email !== null && mail !== email));

  return (
    <Dialog open={user !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent key={user?.id ?? "none"}>
        <DialogHeader>
          <DialogTitle>{user?.displayName ?? ""}</DialogTitle>
          <DialogDescription>
            {ROLE_LABELS[user?.role ?? ""] ?? user?.role}
            {email && ` · ${email}`}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-5">
          {freshPassword && (
            <div className="flex items-center gap-2 rounded-xl bg-primary/10 px-3 py-2">
              <span className="min-w-0 flex-1 font-mono text-[13px] text-brand">
                {freshPassword}
              </span>
              <span className="flex-none text-[11px] text-muted-foreground">
                виден только сейчас
              </span>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Скопировать пароль"
                title="Скопировать"
                onClick={() => void navigator.clipboard?.writeText(freshPassword)}
                className="flex-none text-muted-foreground"
              >
                <CopyIcon className="size-4" />
              </Button>
            </div>
          )}

          {canUpdate && email !== null && user && (
            <Field label="Имя и почта">
              <div className="flex flex-col gap-2">
                <input
                  value={name || user.displayName}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Имя и фамилия"
                  aria-label="Имя сотрудника"
                  className={FIELD}
                />
                <input
                  value={mail || email}
                  onChange={(e) => setMail(e.target.value)}
                  placeholder="Логин — email"
                  aria-label="Почта сотрудника"
                  className={FIELD}
                />
                <div>
                  <Button
                    size="sm"
                    disabled={busy || !changed}
                    onClick={() => {
                      const patch: { displayName?: string; email?: string } = {};
                      if (name && name !== user.displayName) {
                        patch.displayName = name.trim();
                      }
                      if (mail && mail !== email) patch.email = mail.trim();
                      onSave(patch);
                    }}
                  >
                    Сохранить
                  </Button>
                </div>
              </div>
            </Field>
          )}

          {canChangeRole && user && (
            <Field label="Роль">
              <select
                value={user.role}
                disabled={busy}
                aria-label="Роль сотрудника"
                className={cn(FIELD, "w-full")}
                onChange={(e) => onRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {canManageTeams && user && (
            <Field label="Команда">
              <select
                value={folderId ?? ""}
                disabled={busy}
                aria-label="Команда сотрудника"
                className={cn(FIELD, "w-full")}
                onChange={(e) => onTeam(e.target.value || null)}
              >
                <option value="">Без команды</option>
                {roots.map((root) => (
                  <optgroup key={root.id} label={root.name}>
                    <option value={root.id}>{root.name}</option>
                    {teams
                      .filter((t) => t.parentId === root.id)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </Field>
          )}

          {canChangeStatus && user && (
            <Field label="Состояние">
              <select
                value={user.status}
                disabled={busy}
                aria-label="Состояние сотрудника"
                className={cn(FIELD, "w-full")}
                onChange={(e) => onStatus(e.target.value)}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                Отключённый теряет доступ немедленно, но остаётся в журнале
                действий.
              </p>
            </Field>
          )}

          {canUpdate && email !== null && (
            <Field label="Пароль">
              <p className="mb-2 text-[11px] leading-relaxed text-muted-foreground">
                Текущий пароль показать нельзя — в базе лежит хеш. Можно задать
                новый: он появится здесь один раз, чтобы было что передать
                сотруднику.
              </p>
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => onPassword(generatePassword())}
              >
                Задать новый пароль
              </Button>
            </Field>
          )}

          {canChangeStatus && user && (
            <Field label="Увольнение">
              {!confirmDelete ? (
                <>
                  <p className="mb-2 text-[11px] leading-relaxed text-muted-foreground">
                    Удаление стирает карточку насовсем. Оно возможно, только
                    пока за сотрудником ничего не числится — ни записей в
                    журнале, ни отправленных сообщений. У работавшего человека
                    история должна сохранить автора, поэтому его отключают, а не
                    удаляют.
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => setConfirmDelete(true)}
                    className="gap-1.5 text-muted-foreground hover:text-destructive"
                  >
                    <TrashIcon className="size-4" />
                    Удалить сотрудника
                  </Button>
                </>
              ) : (
                <div className="flex flex-col gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-3">
                  <p className="text-sm text-heading">
                    Удалить «{user.displayName}» насовсем?
                  </p>
                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                    Действие необратимо. Если за сотрудником числится история,
                    сервер откажет и объяснит, что именно мешает.
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={onDelete}
                    >
                      Да, удалить
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => setConfirmDelete(false)}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              )}
            </Field>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

/** Приём сотрудника. Пароль генерируется сразу — придумывать его незачем. */
function CreateDialog({
  open,
  busy,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: {
    email: string;
    displayName: string;
    role: string;
    password: string;
  }) => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("MANAGER");
  const [password, setPassword] = useState(generatePassword);

  const ready =
    email.trim().length > 3 &&
    displayName.trim().length > 1 &&
    password.length >= 12;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новый сотрудник</DialogTitle>
          <DialogDescription>
            Пароль показывается один раз — передайте его сотруднику, он сменит
            его при первом входе.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Имя и фамилия"
            aria-label="Имя и фамилия"
            className={FIELD}
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Логин — email"
            aria-label="Логин — email"
            className={FIELD}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            aria-label="Роль"
            className={FIELD}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Пароль"
              aria-label="Пароль"
              className={cn(FIELD, "min-w-0 flex-1 font-mono text-[13px]")}
            />
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => setPassword(generatePassword())}
              className="flex-none"
            >
              Другой
            </Button>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            size="sm"
            disabled={busy || !ready}
            onClick={() =>
              onSubmit({
                email: email.trim(),
                displayName: displayName.trim(),
                role,
                password,
              })
            }
          >
            Принять
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function generatePassword(): string {
  const alphabet =
    "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*";
  const bytes = new Uint32Array(18);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => alphabet[b % alphabet.length]).join("");
}
